import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BsX } from "react-icons/bs";
import AddPolicyForm from "../AddPolicyForm/Policyform";
import Header from "../Header";
import LeftNavBar from "../LeftNavBar";
import PageHeader from "../PageHeader";
import { SecondaryButton } from "../../StyledCss/styledbtn";
import { PageHeaderName } from "../../StyledCss/styledComponents";
import { PrimaryButton } from "../../StyledCss/styledbtn";
import { getPolicyPdfList, deletePolicies } from "../../api/services/hroneApi";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";
import LoadingView from "../LoadingView";
import PopupModal from "../PopupModal";
import PolicyRequestsTab from "../PolicyRequestsTab";
import PolicyHistoryTab from "../PolicyHistoryTab";
import "./policies.css";
import toast from "react-hot-toast";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";

const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

const getTabFromPath = (pathname) => {
  if (pathname === "/policies/request") return "Requests";
  if (pathname === "/policies/history") return "History";
  return "Policies";
};

const Policies = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { hasPermission } = usePermissions();
  const canCreatePolicy = hasPermission(FEATURE_CODES.POLICY_CREATE);
  const canEditPolicy = hasPermission(FEATURE_CODES.POLICY_EDIT);
  const canDeletePolicy = hasPermission(FEATURE_CODES.POLICY_DELETE);
  const canViewRequests = hasPermission(FEATURE_CODES.POLICY_REQUEST_VIEW);
  const canViewHistory = hasPermission(FEATURE_CODES.POLICY_HISTORY_VIEW);
  const pageTabsList = [
    { id: 1, name: "Policies", isVisible: true },
    { id: 2, name: "Requests", isVisible: canViewRequests },
    { id: 3, name: "History", isVisible: canViewHistory },
  ];

  const [activePolicyName, setActivePolicyName] = useState("");
  const [policyName, setPolicyName] = useState("");
  const fileImage = process.env.PUBLIC_URL + "/pdfImage.png";
  const [addPolicyModal, setAddPolicyModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [policiesList, setPoliciesList] = useState([]);
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));
  const pdfViewerGuardRef = useRef(false);

  const [apiStatus, setApiStatus] = useState(apiStatusConstants.initial);
  const [loadingStates, setLoadingStates] = useState({ deletePolicy: false });

  const setLoading = (operation, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [operation]: isLoading }));
  };

  useEffect(() => {
    getPolicies();
  }, []);

  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (!activePolicyName) return;
    window.history.pushState(null, "", window.location.href);
    pdfViewerGuardRef.current = true;
    const handlePopState = () => {
      setActivePolicyName("");
      setPolicyName("");
      pdfViewerGuardRef.current = false;
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [activePolicyName]);

  const handleTabClick = (tabName) => {
    if (tabName === "Requests") navigate("/policies/request");
    else if (tabName === "History") navigate("/policies/history");
    else navigate("/policies");
  };

  const closeViewer = () => {
    if (pdfViewerGuardRef.current) {
      window.history.back();
    } else {
      setActivePolicyName("");
      setPolicyName("");
    }
  };

  const orgDetails = JSON.parse(localStorage.getItem("org"));
  const { primaryColor } = orgDetails;

  const getPolicies = async () => {
    try {
      setApiStatus(apiStatusConstants.inProgress);
      const response = await getPolicyPdfList();
      if (response.status === 200) {
        setApiStatus(apiStatusConstants.success);
        const json = await response.json();
        const list = Array.isArray(json) ? json : (json?.data ?? []);
        const updatedData = list.map((eachItem) => convertKeysToCamelCase(eachItem));
        setPoliciesList(updatedData);
      } else {
        setApiStatus(apiStatusConstants.failure);
      }
    } catch (error) {
      setApiStatus(apiStatusConstants.failure);
    }
  };

  const handleViewRequestPolicy = useCallback((requestData) => {
    setPolicyName(requestData.policyName);
    setActivePolicyName(requestData.policyUrl || "");
  }, []);

  const policyPdfModal = () => (
    <div className="modal-overlay">
      <div className="pdf-container">
        <div className="pdf-heading-container">
          <h4>{policyName}</h4>
          <button className="cross-icon" onClick={closeViewer}>
            <BsX style={{ fontSize: "30px" }} />
          </button>
        </div>
        <iframe
          title="PDF Viewer"
          src={activePolicyName ? `${activePolicyName}#navpanes=0` : null}
          width="100%"
          style={{ border: "none", flex: 1 }}
        ></iframe>
      </div>
    </div>
  );

  const onCloseModal = () => {
    setAddPolicyModal(false);
    setIsEditMode(false);
    setEditingPolicy(null);
  };

  const handlePolicyFormSuccess = () => {
    setSelectedPolicies([]);
  };

  const handleEdit = () => {
    const policy = policiesList.find((p) => p.id === selectedPolicies[0]);
    setEditingPolicy(policy);
    setIsEditMode(true);
    setAddPolicyModal(true);
  };

  const handleSelectPolicy = (policyId) => {
    setSelectedPolicies((prev) =>
      prev.includes(policyId) ? prev.filter((id) => id !== policyId) : [...prev, policyId]
    );
  };

  const renderPoliciesList = () => (
    <>
      {policiesList.length === 0 ? (
        <div className="h-100 d-flex justify-content-center align-items-center">
          <p>No Policies</p>
        </div>
      ) : (
        <div className="policies">
          {policiesList.map((eachPolicy) => (
            <div
              key={eachPolicy.id}
              className="policy-card"
              style={{ backgroundColor: "#ffffff", position: "relative" }}
            >
              {(canEditPolicy || canDeletePolicy) && (
                <input
                  type="checkbox"
                  checked={selectedPolicies.includes(eachPolicy.id)}
                  onChange={() => handleSelectPolicy(eachPolicy.id)}
                  style={{ position: "absolute", top: "10px", right: "10px" }}
                />
              )}
              <button
                style={{ backgroundColor: "transparent", border: "none", width: "100%" }}
                onClick={() => {
                  setActivePolicyName(eachPolicy.policyUrl);
                  setPolicyName(eachPolicy.policyName);
                }}
              >
                <img className="pdfImage" src={fileImage} alt="file" />
                <div>
                  <p className="cardText" title={eachPolicy.policyName}>
                    {eachPolicy.policyName}
                  </p>
                </div>
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const renderPolicies = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
        return <LoadingView />;
      case apiStatusConstants.success:
        return renderPoliciesList();
      case apiStatusConstants.failure:
        return null;
      default:
        break;
    }
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setSelectedPolicies([]);
  };

  const confirmDelete = async () => {
    try {
      setLoading("deletePolicy", true);
      const response = await deletePolicies(selectedPolicies);
      if (response.status === 200) {
        toast.success("Policy Deleted Successfully");
        await getPolicies();
        setIsDeleteModalOpen(false);
        setSelectedPolicies([]);
      } else {
        toast.error("Failed to delete policy");
      }
    } catch (error) {
      console.error("Error deleting policy:", error);
      toast.error("Failed to delete policy");
    } finally {
      setLoading("deletePolicy", false);
    }
  };

  const tabs = () => (
    <div className="d-flex align-items-center policy-tabs-container">
      {pageTabsList.map(
        (eachTab) =>
          eachTab.isVisible && (
            <button
              style={
                activeTab === eachTab.name
                  ? { color: primaryColor, borderBottom: `3px solid ${primaryColor}` }
                  : {}
              }
              className={activeTab === eachTab.name ? "policy-active-tab" : "policy-tab"}
              onClick={() => handleTabClick(eachTab.name)}
              key={eachTab.id}
            >
              {eachTab.name}
            </button>
          )
      )}
    </div>
  );

  const renderActiveTabPage = () => {
    switch (activeTab) {
      case "Policies":
        return <div className="policy-cards-content">{renderPolicies()}</div>;
      case "Requests":
        return <PolicyRequestsTab onView={handleViewRequestPolicy} />;
      case "History":
        return <PolicyHistoryTab onView={handleViewRequestPolicy} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <Header />
      <div className="d-flex">
        <LeftNavBar />
        <div className="page-content-wrapper">
          <PageHeader>
            <PageHeaderName>Policies</PageHeaderName>
            <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
              {canDeletePolicy && activeTab === "Policies" && (
                <PrimaryButton
                  onClick={handleDelete}
                  disabled={selectedPolicies.length === 0}
                >
                  Delete
                </PrimaryButton>
              )}
              {canEditPolicy && activeTab === "Policies" && (
                <PrimaryButton
                  onClick={handleEdit}
                  disabled={selectedPolicies.length !== 1}
                >
                  Edit
                </PrimaryButton>
              )}
              {canCreatePolicy && (
                <PrimaryButton onClick={() => setAddPolicyModal(!addPolicyModal)}>
                  Add Policy
                </PrimaryButton>
              )}
            </div>
          </PageHeader>

          <div className="policy-content-box">
            {tabs()}
            {renderActiveTabPage()}
          </div>

          {activePolicyName && policyPdfModal()}

          <PopupModal
            modalName={isEditMode ? "Edit Policy" : "Add Policy"}
            closeBtn={onCloseModal}
            showModal={addPolicyModal}
          >
            <AddPolicyForm
              getPolicies={getPolicies}
              closeModal={onCloseModal}
              isEditMode={isEditMode}
              editingPolicy={editingPolicy}
              onSuccess={handlePolicyFormSuccess}
            />
          </PopupModal>
        </div>
      </div>

      <div className="no-modal-mb">
        <PopupModal
          showModal={isDeleteModalOpen}
          modalName="Confirm Delete"
          closeBtn={cancelDelete}
          marginBottom="0px"
        >
          <div>
            <p>Are you sure you want to delete the policies?</p>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "end",
                marginTop: "15px",
              }}
            >
              <SecondaryButton onClick={cancelDelete}>Cancel</SecondaryButton>
              <PrimaryButton onClick={confirmDelete}>
                {loadingStates.deletePolicy ? "Deleting..." : "Delete"}
              </PrimaryButton>
            </div>
          </div>
        </PopupModal>
      </div>
    </div>
  );
};

export default Policies;
